import { useState, useEffect } from "react";
import { IoClose, IoCheckmark } from "react-icons/io5";
import Card from "components/card";
import { API_BASE_URL } from "utils/api";

const folderColors = [
  { name: "Purple Blue", colors: ["#8B5CF6", "#07f807"], angle: "135deg" },
  { name: "Pink Orange", colors: ["#EC4899", "#F97316"], angle: "135deg" },
  { name: "Teal Cyan", colors: ["#14B8A6", "#06B6D4"], angle: "135deg" },
  { name: "Blue Indigo", colors: ["#3B82F6", "#6366F1"], angle: "135deg" },
  { name: "Emerald Green", colors: ["#10B981", "#34D399"], angle: "135deg" },
  { name: "Rose Pink", colors: ["#F43F5E", "#FB7185"], angle: "135deg" },
  { name: "Amber Yellow", colors: ["#F59E0B", "#FBBF24"], angle: "135deg" },
  { name: "Slate Gray", colors: ["#64748B", "#94A3B8"], angle: "135deg" },
];

const CreateFolderModal = ({ isOpen, onClose, onSubmit, folderType = "Lead" }) => {
  const [folderName, setFolderName] = useState("");
  const [selectedColor, setSelectedColor] = useState(folderColors[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "error" });

  const MAX_FOLDER_NAME_LENGTH = 15;
  const getCharacterCount = (text) => text.length;

  const handleFolderNameChange = (value) => {
    if (value.length <= MAX_FOLDER_NAME_LENGTH) {
      setFolderName(value);
      return;
    }
    setFolderName(value.slice(0, MAX_FOLDER_NAME_LENGTH));
  };

  // automatically hide toast after 3 seconds
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", type: "error" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  if (!isOpen) return null;

  const validateForm = () => {
    if (!folderName.trim()) {
      setError("Folder name is required");
      return false;
    }
    if (folderName.trim().length < 3) {
      setError("Folder name must be at least 3 characters");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError("");

    if (!validateForm()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/folders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: folderName.trim(),
          type: folderType,
          gradient: selectedColor,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        // backend validation failures
        if (Array.isArray(result.errors) && result.errors.length) {
          const msgs = result.errors.map(err => err.msg).join(" ");
          setError(msgs);
          setToast({ show: true, message: msgs, type: "error" });
        } else {
          const message = result.message || "Failed to create folder";
          setError(message);
          setToast({ show: true, message, type: "error" });
        }
        return;
      }

      // success
      setToast({ show: true, message: result.message || "Folder created successfully", type: "success" });
      onSubmit(result.data);
      resetForm();
      onClose();
    } catch (err) {
      const msg = err.message || "An error occurred while creating the folder";
      setError(msg);
      setToast({ show: true, message: msg, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFolderName("");
    setSelectedColor(folderColors[0]);
    setError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* toast/banner */}
      {toast.show && (
        <div className={`absolute top-4 z-50 mx-auto w-full max-w-md rounded p-3 text-center text-white ${toast.type === "success" ? "bg-green-500" : "bg-red-500"
          }`}>
          {toast.message}
        </div>
      )}

      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative w-full max-w-md px-4">
        <Card extra="w-full max-w-md p-6 mx-4">
          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
              Create New Folder
            </h3>
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 disabled:opacity-50"
            >
              <IoClose className="text-xl" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/30 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="mb-6">
            <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
              Folder Name
            </label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => handleFolderNameChange(e.target.value)}
              placeholder="Enter folder name"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none disabled:opacity-50 dark:border-white/10 dark:text-white"
              autoFocus
            />
            <p className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>{getCharacterCount(folderName)}/{MAX_FOLDER_NAME_LENGTH} characters</span>
              <span>{Math.max(0, MAX_FOLDER_NAME_LENGTH - getCharacterCount(folderName))} remaining</span>
            </p>
          </div>

          <div className="mb-6">
            <label className="mb-3 block text-sm font-bold text-navy-700 dark:text-white">
              Folder Color
            </label>
            <div className="grid grid-cols-4 gap-3">
              {folderColors.map((color, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedColor(color)}
                  disabled={loading}
                  className={`group relative h-12 w-full rounded-xl transition-all duration-200 hover:scale-105 disabled:opacity-50 ${selectedColor.name === color.name
                    ? "ring-2 ring-brand-500 ring-offset-2"
                    : "hover:ring-2 hover:ring-gray-300 hover:ring-offset-1"
                    }`}
                  style={{
                    background: `linear-gradient(${color.angle}, ${color.colors.join(', ')})`,
                  }}
                  title={color.name}
                >
                  {selectedColor.name === color.name && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <IoCheckmark className="text-xl font-bold text-white drop-shadow-lg" />
                    </div>
                  )}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Selected: <span className="font-medium text-navy-700 dark:text-white">{selectedColor.name}</span>
            </p>
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
              Preview
            </label>
            <div className="flex min-w-0 items-center gap-3 rounded-xl border border-gray-200 p-3 dark:border-white/10">
              <div
                className="h-10 w-10 flex-shrink-0 rounded-lg"
                style={{
                  background: `linear-gradient(${selectedColor.angle}, ${selectedColor.colors.join(', ')})`,
                }}
              />
              <span className="min-w-0 truncate text-sm font-medium text-navy-700 dark:text-white">
                {folderName || "Folder Name"}
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={loading}
              className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!folderName.trim() || loading}
              className="flex h-12 w-full items-center justify-center rounded-xl bg-brand-500 text-sm font-bold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Folder"}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CreateFolderModal;
